/**
 * What the floating widget writes to `activities`, so its adoption and engagement can be read
 * against the /alfie page (ClickUp 86d4ayx6w).
 *
 * Every row is `type: 'alfie'`, `entity_type: 'widget'`, with `surface: 'widget'` in its
 * metadata; the send itself is logged by `trackAlfieMessageSent` in api/alfie.ts with the same
 * surface stamp. Tracking never blocks the UI and never throws: `trackWidget` swallows.
 *
 * Questions the rows answer, with the SQL, in hermes-alfie/docs/alfie/ALFIE_WIDGET_ANALYTICS.md:
 * weekly widget users, share of messages by surface, sends per open, opens with no send,
 * escapes to the full page, week-over-week return.
 */
import { type Activity, api } from '@/api/client';

export type WidgetOpenTrigger = 'launcher' | 'shortcut' | 'event';

export type WidgetEvent =
  | { action: 'opened'; trigger: WidgetOpenTrigger; route: string; threadId: string | null }
  | {
    action: 'closed';
    route: string;
    threadId: string | null;
    secondsOpen: number;
    messagesSent: number;
  }
  | { action: 'history_opened'; threadId: string | null }
  | { action: 'prompt_clicked'; prompt: string }
  | { action: 'thread_pinned' | 'thread_unpinned' | 'thread_renamed' | 'thread_deleted'; threadId: string }
  | { action: 'opened_full_page'; route: string; threadId: string | null };

export type WidgetActivity = Omit<Activity, 'id' | 'user_id' | 'created_at'>;

const DESCRIPTIONS: Record<WidgetEvent['action'], string> = {
  opened: 'Opened the Alfie widget',
  closed: 'Closed the Alfie widget',
  history_opened: 'Opened the Alfie widget history',
  prompt_clicked: 'Picked a starter prompt in the Alfie widget',
  thread_pinned: 'Pinned a thread in the Alfie widget',
  thread_unpinned: 'Unpinned a thread in the Alfie widget',
  thread_renamed: 'Renamed a thread in the Alfie widget',
  thread_deleted: 'Deleted a thread in the Alfie widget',
  opened_full_page: 'Opened the full Alfie page from the widget',
};

/** The `activities` row for one widget event. Pure. */
export function widgetActivity(event: WidgetEvent): WidgetActivity {
  const { action, ...rest } = event;
  const threadId = 'threadId' in rest ? rest.threadId : null;
  const entityName = action === 'prompt_clicked' ? event.prompt.slice(0, 80) : null;
  return {
    type: 'alfie',
    action,
    entity_type: 'widget',
    // `entity_id` is not nullable: a widget event with no thread yet keys on the widget itself.
    entity_id: threadId ?? 'widget',
    entity_name: entityName,
    description: DESCRIPTIONS[action],
    metadata: { surface: 'widget', ...rest },
  };
}

/** Fire and forget. A tracking failure is not the member's problem. */
export function trackWidget(event: WidgetEvent): void {
  try {
    void api.logActivity(widgetActivity(event)).catch(() => {});
  } catch {
    // logActivity itself never throws synchronously, but the widget must not depend on it.
  }
}
